import { LevelObjective } from '@/levels/LevelData';

/** Tracks progress towards a level objective and reports completion exactly once. */
export class ObjectiveTracker {
  private kills = 0;
  private elapsedMs = 0;
  private completed = false;

  constructor(private readonly objective: LevelObjective) {}

  /** Returns true on the kill that completes the objective. */
  recordKill(): boolean {
    this.kills++;
    return this.objective.type === 'killEnemies' && this.checkCompletion(this.kills >= this.objective.target);
  }

  /** Returns true on the tick that completes the objective. */
  recordTime(deltaMs: number): boolean {
    this.elapsedMs += deltaMs;
    return this.objective.type === 'survive' && this.checkCompletion(this.elapsedMs >= this.objective.target * 1000);
  }

  isComplete(): boolean {
    return this.completed;
  }

  getKills(): number {
    return this.kills;
  }

  describe(): string {
    return this.objective.type === 'survive'
      ? `Survive ${this.objective.target} seconds`
      : `Kill ${this.objective.target} enemies`;
  }

  progressText(): string {
    if (this.objective.type === 'survive') {
      const remaining = Math.max(0, Math.ceil(this.objective.target - this.elapsedMs / 1000));
      const minutes = Math.floor(remaining / 60);
      const seconds = (remaining % 60).toString().padStart(2, '0');
      return `${minutes}:${seconds} left`;
    }
    return `${Math.min(this.kills, this.objective.target)}/${this.objective.target}`;
  }

  progressFraction(): number {
    const progress = this.objective.type === 'survive'
      ? this.elapsedMs / 1000 / this.objective.target
      : this.kills / this.objective.target;
    return Math.min(1, progress);
  }

  private checkCompletion(reached: boolean): boolean {
    if (this.completed || !reached) return false;
    this.completed = true;
    return true;
  }
}
